import { Button } from '@components/ui/button';
import { AlertCircle, EyeOff } from 'lucide-react';
import React from 'react';

interface HintBannerProps {
  /**
   * The hint/warning text to display
   */
  hint: string;

  /**
   * Optional className for custom styling
   */
  className?: string;

  /**
   * Optional callback when user clicks hide button (shows Eye icon when provided)
   */
  onHide?: () => void;
}

/**
 * HintBanner - A yellow warning banner component
 * Displays contextual hints or warnings for form fields.
 *
 * Example: "ZIP code 97829 is in Oregon, not Georgia — may need correction"
 *
 * @component
 * @example
 * ```tsx
 * <HintBanner hint="Last digit differs from account records — verify with caller" />
 * ```
 */
export const HintBanner: React.FC<HintBannerProps> = ({ hint, className = '', onHide }) => {
  return (
    <div
      className={`flex items-start gap-2 rounded-md border border-ai-hint-border bg-ai-hint px-3 py-1.5 ${className}`}
      role="alert"
      aria-live="polite"
    >
      <AlertCircle className="h-4 w-4 text-ai-hint-icon shrink-0 mt-0.5" aria-hidden="true" />
      <span className="text-sm text-ai-hint-foreground text-left flex-1">{hint}</span>
      {onHide && (
        <Button
          variant="ghost"
          size="sm"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onHide();
          }}
          className="h-6 w-6 p-0 text-ai-hint-foreground hover:bg-ai-hint-border hover:text-ai-hint-foreground"
          title="Hide hint"
          aria-label="Hide hint"
        >
          <EyeOff className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

HintBanner.displayName = 'HintBanner';
